/**
 * BFCM Customer Videos carousel.
 * Centre-focused loop: each card sits at (offset from active card) × (card width + gap).
 * Only the centre card loads and plays its video (muted by default).
 * Auto-advances, holding while the sound is on, while dragging, while the visitor has
 * paused the video, while the carousel is off screen, or when reduced motion is preferred.
 */
if (!customElements.get('bfcm-ugc-carousel')) {
  customElements.define(
    'bfcm-ugc-carousel',
    class BfcmUgcCarousel extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('[data-ugc-stage]');
        this.cards = Array.from(this.querySelectorAll('[data-ugc-card]'));
        if (!this.stage || this.cards.length === 0) return;

        this.index = 0;
        this.offsets = [];
        this.muted = true;
        this.userPaused = false;
        this.visible = true;
        this.dragging = false;
        this.suppressClick = false;
        this.speed = Number(this.dataset.speed) || 5000;
        this.autoplay =
          this.dataset.autoplay === 'true' &&
          this.cards.length > 1 &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Removed on disconnect, so a reconnect (theme editor reorder) never doubles them
        this.listeners = new AbortController();
        const signal = this.listeners.signal;

        this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.go(this.index - 1), { signal });
        this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.go(this.index + 1), { signal });

        // Capture phase so a drag never opens a link or adds to cart on release
        this.stage.addEventListener('click', this.onClickCapture.bind(this), { capture: true, signal });
        this.stage.addEventListener('click', this.onClick.bind(this), { signal });
        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this), { signal });

        window.addEventListener('resize', () => this.render(true), { signal });

        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            this.syncVideo();
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);

        // Theme editor: centre the selected block (instantly after a re-render) and stop auto-advancing
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const i = this.cards.indexOf(event.target);
            if (i > -1) {
              this.editorHold = true;
              this.go(i, Boolean(event.detail?.load));
            }
          },
          { signal }
        );
        this.addEventListener(
          'shopify:block:deselect',
          () => {
            this.editorHold = false;
            this.restartTimer();
          },
          { signal }
        );

        this.render(true);
        this.restartTimer();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.listeners?.abort();
        this.observer?.disconnect();
      }

      get step() {
        const styles = getComputedStyle(this);
        return parseFloat(styles.getPropertyValue('--ugc-w')) + parseFloat(styles.getPropertyValue('--ugc-gap'));
      }

      offsetFor(i) {
        const n = this.cards.length;
        let off = (((i - this.index) % n) + n) % n;
        if (off > n / 2) off -= n;
        return off;
      }

      go(i, instant = false) {
        const n = this.cards.length;
        this.index = ((i % n) + n) % n;
        this.userPaused = false;
        this.render(instant);
        this.restartTimer();
      }

      render(instant = false) {
        const step = this.step;
        this.cards.forEach((card, i) => {
          const off = this.offsetFor(i);
          const prev = this.offsets[i];
          // Cards that wrap from one end to the other jump instead of sliding across
          if (instant || (prev !== undefined && Math.abs(off - prev) > 1)) {
            card.classList.add('no-anim');
            requestAnimationFrame(() => requestAnimationFrame(() => card.classList.remove('no-anim')));
          }
          card.style.setProperty('--ugc-x', `${off * step}px`);
          card.style.zIndex = String(10 - Math.abs(off));
          card.classList.toggle('is-active', off === 0);
          this.offsets[i] = off;
        });
        this.syncVideo();
      }

      syncVideo() {
        this.cards.forEach((card, i) => {
          const holder = card.querySelector('[data-ugc-video]');
          if (!holder) return;
          let video = holder.querySelector('video');

          if (this.offsets[i] === 0 && this.visible) {
            if (!video && holder.dataset.src) video = this.createVideo(holder);
            if (!video) return;
            video.muted = this.muted;
            if (!this.userPaused) video.play().catch(() => {});
            this.updateControls(holder, video);
          } else if (video && !video.paused) {
            video.pause();
          }
        });
      }

      createVideo(holder) {
        const video = document.createElement('video');
        video.src = holder.dataset.src;
        if (holder.dataset.poster) video.poster = holder.dataset.poster;
        video.loop = true;
        video.muted = this.muted;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'metadata';
        holder.appendChild(video);

        const controls = document.createElement('div');
        controls.className = 'bfcm-ugc__ctrl';
        controls.innerHTML =
          '<button type="button" class="bfcm-ugc__sound"><i class="ph ph-speaker-slash" aria-hidden="true"></i></button>' +
          '<button type="button" class="bfcm-ugc__play"><i class="ph ph-pause" aria-hidden="true"></i></button>';
        holder.appendChild(controls);

        controls.querySelector('.bfcm-ugc__sound').addEventListener('click', () => {
          this.muted = !this.muted;
          this.syncVideo();
          this.restartTimer();
        });
        controls.querySelector('.bfcm-ugc__play').addEventListener('click', () => {
          if (video.paused) {
            this.userPaused = false;
            video.play().catch(() => {});
          } else {
            this.userPaused = true;
            video.pause();
          }
          this.restartTimer();
        });

        const update = () => this.updateControls(holder, video);
        video.addEventListener('play', update);
        video.addEventListener('pause', update);
        return video;
      }

      updateControls(holder, video) {
        const sound = holder.querySelector('.bfcm-ugc__sound');
        const play = holder.querySelector('.bfcm-ugc__play');
        if (sound) {
          sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          sound.querySelector('i').className = `ph ${video.muted ? 'ph-speaker-slash' : 'ph-speaker-high'}`;
        }
        if (play) {
          play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
          play.querySelector('i').className = `ph ${video.paused ? 'ph-play' : 'ph-pause'}`;
        }
      }

      restartTimer() {
        clearTimeout(this.timer);
        if (!this.autoplay) return;
        this.timer = setTimeout(() => {
          const hold =
            this.dragging || !this.muted || this.userPaused || !this.visible || this.editorHold || document.hidden;
          if (hold) {
            this.restartTimer();
          } else {
            this.go(this.index + 1);
          }
        }, this.speed);
      }

      onClickCapture(event) {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }

      onClick(event) {
        if (event.target.closest('a, button, input')) return;
        const card = event.target.closest('[data-ugc-card]');
        const i = this.cards.indexOf(card);
        if (i > -1 && this.offsets[i] !== 0) this.go(i);
      }

      onPointerDown(event) {
        if (event.button !== 0 || this.cards.length < 2) return;
        if (event.target.closest('a, button, input')) return;

        const startX = event.clientX;
        const pointerId = event.pointerId;
        let dx = 0;
        let moved = false;
        this.dragging = true;

        const onMove = (e) => {
          if (e.pointerId !== pointerId) return;
          dx = e.clientX - startX;
          if (!moved && Math.abs(dx) > 5) {
            moved = true;
            this.classList.add('is-dragging');
          }
          if (moved) this.style.setProperty('--ugc-drag', `${dx}px`);
        };

        const onUp = (e) => {
          if (e.pointerId !== pointerId) return;
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          this.dragging = false;
          this.classList.remove('is-dragging');
          this.style.setProperty('--ugc-drag', '0px');
          if (!moved) return;

          this.suppressClick = true;
          setTimeout(() => (this.suppressClick = false), 0);

          let steps = Math.round(-dx / this.step);
          if (steps === 0 && Math.abs(dx) > 40) steps = dx < 0 ? 1 : -1;
          if (steps !== 0) {
            this.go(this.index + steps);
          } else {
            this.restartTimer();
          }
        };

        const { signal } = this.listeners;
        window.addEventListener('pointermove', onMove, { signal });
        window.addEventListener('pointerup', onUp, { signal });
        window.addEventListener('pointercancel', onUp, { signal });
      }
    }
  );
}
